import { isAdmin } from '@/utils/adminProRedirect'
import { formatUberPickupWindow } from '@/utils/calendar'
import clsx from 'clsx'
import { format } from 'date-fns'
import Button from 'lib/components/Button/Button'
import DeliverySteps from 'lib/components/DeliverySteps/DeliverySteps'
import Icon from 'lib/components/Icon/Icon'
import Link from 'lib/components/Link/Link'
import { ModalButton, useModal } from 'lib/components/Modal/Modal'
import NoteModal from 'lib/components/NoteModal/NoteModal'
import ShipmentDetailsModal, {
  ShipmentDetailsModalProps,
} from 'lib/components/ShipmentDetailsModal/ShipmentDetailsModal'
import {
  getShipmentStatusIcon,
  getShipmentStatusMeta,
} from 'lib/settings/order'
import type { AdminDetails } from 'lib/types/mock/admin-details'
import formatShipmentStatusMessage from 'lib/utils/formatShipmentStatusMessage'
import { formatTrackingIdLink } from 'lib/utils/orderUtils'
import { getDeliveryStatus, parseDate } from 'lib/utils/routeSnapshotUtils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { RouteSnapshotButtonHandlers } from '../RouteSnapshot'

export interface RouteSnapshotShipmentProps {
  shipment: OrderShipment
  leg?: OrderLeg // Add leg for international detection
  /**
   * Whether the leg is an Uber pickup
   */
  uberPickup?: boolean
  /**
   * ISO timestamp for Uber pickup window start
   */
  pickupReadyTime?: string
  /**
   * ISO timestamp for Uber pickup window end
   */
  pickupCloseTime?: string
  /**
   * IANA timezone of the origin address for formatting the pickup window
   */
  originTimeZone?: string
  buttonHandlers?: RouteSnapshotButtonHandlers
  getShipmentDetails?: (
    tracking_id: string,
  ) => Promise<Omit<ShipmentDetailsModalProps, 'id'>>
  onClickShipmentDetails?: () => void // Optional for custom handling
  adminDetail?: DeepPartial<AdminDetails>
  formatTrackingIdLink?: (trackingId: string) => string
  isUserAuthenticated?: boolean
  options?: Record<string, any>
  activeShipmentId?: string
  /**
   * Order user ID for permission checks
   */
  orderUserId?: string
  /**
   * Current logged in user ID for permission checks
   */
  currentUserId?: string | null
  /**
   * Current user roles for permission checks
   */
  userRoles?: string[]
  /**
   * Allow unauthenticated guests to see the print label button
   */
  allowGuestPrintLabel?: boolean
}

const defaultOptions = {
  showShippingDetails: true,
  shipmentClass: 'bg-neutral-200 p-6',
}

const NODE_MODAL_TITLE = 'Add note'

export default function RouteSnapshotShipment({
  shipment,
  leg,
  uberPickup,
  pickupReadyTime,
  pickupCloseTime,
  originTimeZone,
  buttonHandlers,
  getShipmentDetails,
  onClickShipmentDetails,
  adminDetail,
  isUserAuthenticated = false,
  activeShipmentId,
  options = {},
  orderUserId,
  currentUserId,
  userRoles,
  allowGuestPrintLabel,
}: RouteSnapshotShipmentProps) {
  const [openDialog] = useModal((state) => [state.openDialog])
  const [canViewDetails, setCanViewDetails] = useState(false)
  const [shipmentDetails, setShipmentDetails] = useState<
    Omit<ShipmentDetailsModalProps, 'id'> | undefined
  >()

  const customOptions = useMemo(() => {
    return { ...defaultOptions, ...options }
  }, [options])

  const hasGuestIdParam = useMemo(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).has('guest_id')
  }, [])

  // Get the primary product name (should be a label)
  const primaryProduct = useMemo(() => {
    return shipment.products.find((p) => p.type === 'label') as
      | OrderProductLabel
      | undefined
  }, [shipment.products])

  const productName =
    primaryProduct?.display_name || shipment.name || 'Unknown Item'

  const pickupDate = parseDate(shipment.ship_date || '')
  const deliveryDate = parseDate(shipment.arrival_date || '')

  // Compute the human readable status message via shared util
  const formatMessage = () =>
    formatShipmentStatusMessage(shipment, leg, pickupDate)

  const statusMeta = getShipmentStatusMeta(shipment.state || '')
  const statusLabel = statusMeta?.label || 'Unknown Status'
  const statusMessage = formatMessage()
  const statusIcon = getShipmentStatusIcon(shipment.state || '') || 'clock'

  const deliveryStepStatus = getDeliveryStatus(shipment.state || '')

  useEffect(() => {
    // if this is the active shipment pop the shipment details modal
    if (
      activeShipmentId &&
      activeShipmentId == shipment?.tracking_id &&
      isUserAuthenticated
    ) {
      setCanViewDetails(true)
    }
  }, [activeShipmentId, isUserAuthenticated, shipment?.tracking_id])

  // Determine if a shipment is delayed. A shipment is delayed when:
  // 1. The carrier has pushed the estimated delivery date beyond the original
  //    arrival date (carrier reported delay), OR
  // 2. The delivery date has passed (day after) and the shipment is still not
  //    delivered (overdue)
  const isDelayed = useMemo(() => {
    if (shipment.state === 'delivered') return false

    const carrierEstimate = parseDate(
      shipment.estimated_carrier_delivery_date || '',
    )
    const originalArrival = deliveryDate

    // Check if carrier has pushed back the delivery date
    if (
      carrierEstimate &&
      originalArrival &&
      !isNaN(carrierEstimate.getTime()) &&
      !isNaN(originalArrival.getTime())
    ) {
      const carrierDateOnly = new Date(
        carrierEstimate.getFullYear(),
        carrierEstimate.getMonth(),
        carrierEstimate.getDate(),
      )
      const originalDateOnly = new Date(
        originalArrival.getFullYear(),
        originalArrival.getMonth(),
        originalArrival.getDate(),
      )
      if (carrierDateOnly > originalDateOnly) return true
    }

    // Check if the shipment is overdue (past the delivery date)
    if (originalArrival && !isNaN(originalArrival.getTime())) {
      const dayAfterDelivery = new Date(
        originalArrival.getFullYear(),
        originalArrival.getMonth(),
        originalArrival.getDate() + 1,
      )
      if (new Date() >= dayAfterDelivery) return true
    }

    return false
  }, [deliveryDate, shipment.state, shipment.estimated_carrier_delivery_date])

  // Determine if international based on leg data
  const isInternational = useMemo(() => {
    if (!leg) return false

    // Check if origin and destination countries are different
    const originCountry = leg.origin?.country_code || leg.origin?.country
    const destCountry =
      leg.destination?.country_code || leg.destination?.country

    return (
      originCountry !== destCountry ||
      (originCountry && originCountry !== 'US') ||
      (destCountry && destCountry !== 'US')
    )
  }, [leg])

  // Determine route type from shipment data
  const routeType = useMemo(() => {
    return leg?.leg_index === 1 ? 'outbound' : 'return'
  }, [leg?.leg_index])

  // Check if current user can print labels (both outbound and return)
  // Only allow if user matches order user OR user is admin
  const canPrintLabel = useMemo(() => {
    if (hasGuestIdParam) {
      return true
    }
    // Allow unauthenticated guests to see print button (Sticks/Skis only)
    if (allowGuestPrintLabel) {
      return true
    }
    // If no order user ID, we can't verify ownership, so don't show button
    if (!orderUserId) {
      return false
    }

    // If no current user ID, user is not logged in, so don't show button
    if (!currentUserId) {
      return false
    }

    // Check if current user matches order user
    const isOrderUser = currentUserId === orderUserId

    // Check if current user is admin
    const userIsAdmin = isAdmin(userRoles)

    return isOrderUser || userIsAdmin
  }, [
    allowGuestPrintLabel,
    currentUserId,
    hasGuestIdParam,
    orderUserId,
    userRoles,
  ])

  // Check if current user can view sensitive details (addresses and insurance)
  // Only allow if user matches order user OR user is admin
  const canViewSensitiveDetails = useMemo(() => {
    // If no order user ID, we can't verify ownership, so don't allow
    if (!orderUserId) {
      return false
    }

    // If no current user ID, user is not logged in, so don't allow
    if (!currentUserId) {
      return false
    }

    // Check if current user matches order user
    const isOrderUser = currentUserId === orderUserId

    // Check if current user is admin
    const userIsAdmin = isAdmin(userRoles)

    return isOrderUser || userIsAdmin
  }, [currentUserId, orderUserId, userRoles])

  const shipmentDetailsModalID = useMemo(
    () => `shipment-details-modal-${shipment.tracking_id}`,
    [shipment.tracking_id],
  )

  const noteModalID = useMemo(
    () => `note-modal-${shipment.tracking_id}`,
    [shipment.tracking_id],
  )

  useEffect(() => {
    if (getShipmentDetails && isUserAuthenticated && shipment.tracking_id) {
      const fetchDetails = async () => {
        try {
          const shipmentItemDetails = await getShipmentDetails(
            shipment.tracking_id,
          )
          if (shipmentItemDetails) {
            setShipmentDetails(shipmentItemDetails)
            if (canViewDetails) {
              // open the details when the data is ready
              openDialog(shipmentDetailsModalID)
            }
          }
        } catch (error) {
          console.error('Failed to fetch shipment details:', error)
        }
      }
      fetchDetails()
    }
  }, [
    getShipmentDetails,
    shipment.tracking_id,
    isUserAuthenticated,
    canViewDetails,
    shipmentDetailsModalID,
    openDialog,
  ])

  const handleClickViewDetails = useCallback(() => {
    if (isUserAuthenticated) {
      setCanViewDetails(true)
      openDialog(shipmentDetailsModalID)
    } else {
      onClickShipmentDetails?.()
    }
  }, [
    isUserAuthenticated,
    openDialog,
    shipmentDetailsModalID,
    onClickShipmentDetails,
  ])

  // Check if shipment has been created (has tracking ID)
  const hasTrackingId = Boolean(
    shipment.tracking_id || shipment.carrier_tracking_id,
  )
  const onCTAClick = useCallback(() => {}, [])
  const ctaButtons = useMemo(() => {
    return [
      {
        text: `Add to shipment\n${shipment.tracking_id}`,
        onCTAClick,
      },
      {
        text: `Add to all ${routeType}`,
        onCTAClick,
      },
      {
        text: 'Add to all shipments',
        onCTAClick,
      },
    ]
  }, [routeType, shipment.tracking_id, onCTAClick])

  return (
    <>
      <div
        className={clsx(
          'flex flex-col',
          customOptions.shipmentClass || 'bg-neutral-200 p-6',
        )}
      >
        <div className="lg:flex flex-col gap-y-4 lg:gap-x-8 lg:items-start">
          {customOptions.showShippingDetails && (
            // The tracking id reads as the last line of the shipment's
            // details rather than sitting opposite them: it used to be the
            // right half of a justify-between row, so it floated
            // vertically-centred against the three lines of dates with nothing
            // anchoring it, and wrapped badly once the column narrowed.
            <div className="w-full">
              <strong className="type-body-3">{productName}</strong>
              <p className="mt-2">
                Ship date: {format(pickupDate, 'MMMM d, yyyy')}
              </p>
              <p className="mt-2">
                {shipment.state === 'delivered'
                  ? 'Delivered on'
                  : 'Delivery by'}
                : {format(deliveryDate, 'MMMM d, yyyy')}
              </p>
              {uberPickup && (
                <>
                  <p className="mt-2">Handling: Uber Pickup</p>
                  {pickupReadyTime && pickupCloseTime && (
                    <p className="mt-2">
                      Pickup window:{' '}
                      {formatUberPickupWindow(
                        pickupReadyTime,
                        pickupCloseTime,
                        originTimeZone,
                      )}
                    </p>
                  )}
                </>
              )}
              {hasTrackingId && (
                <p className="mt-2">
                  <a
                    href={formatTrackingIdLink(shipment.tracking_id)}
                    className="underline"
                  >
                    Tracking: {shipment.tracking_id}
                  </a>
                </p>
              )}
            </div>
          )}
          {/* Status with icon */}
          <div className="flex items-center gap-2 mt-4">
            {statusLabel == 'Cancelled' ? (
              <>
                <Icon icon={statusIcon} className="w-6 h-6 text-red-500" />
                <strong className="type-body-3">{statusMessage}</strong>
              </>
            ) : (
              <>
                <Icon icon={statusIcon} className="w-6 h-6" />
                <strong className="type-body-3">{statusMessage}</strong>
              </>
            )}
          </div>

          {statusLabel !== 'Cancelled' && (
            <div className="w-full mt-6">
              <DeliverySteps
                steps={['Order created', 'Shipped', 'Delivered']}
                currentStep={deliveryStepStatus}
                isDelayed={isDelayed}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 w-full mt-6 lg:flex-row lg:items-center">
          {canPrintLabel && statusLabel !== 'Cancelled' && (
            <div className="flex-1 basis-0">
              <Button
                type="button"
                variant="secondary"
                icon="print"
                className="[&]:w-full"
                onClick={() =>
                  buttonHandlers?.printShipment?.(shipment.tracking_id)
                }
              >
                {isInternational
                  ? `Print ${routeType} label & international documents`
                  : `Print ${routeType} label`}
              </Button>
            </div>
          )}
          <div className="flex-1 basis-0">
            <Button
              type="button"
              onClick={handleClickViewDetails}
              className="[&]:w-full"
            >
              View details
            </Button>
          </div>
          {adminDetail && (
            <div className="mt-4 text-right">
              <ModalButton
                targetModal={noteModalID}
                render={({ onClick }) => (
                  <Link
                    className="type-body-11 [&&]:text-neutral-700"
                    onClick={(e) => {
                      e.preventDefault()
                      onClick()
                    }}
                  >
                    Add note
                  </Link>
                )}
              />
            </div>
          )}
        </div>
      </div>
      {shipmentDetails && (
        <ShipmentDetailsModal
          {...shipmentDetails}
          id={shipmentDetailsModalID}
          canViewAddresses={canViewSensitiveDetails}
          canViewInsurance={canViewSensitiveDetails}
          uberPickup={uberPickup}
          pickupReadyTime={pickupReadyTime}
          pickupCloseTime={pickupCloseTime}
          originTimeZone={originTimeZone}
        />
      )}
      {adminDetail && (
        <NoteModal
          id={noteModalID}
          title={NODE_MODAL_TITLE}
          buttons={ctaButtons}
        />
      )}
    </>
  )
}
